import * as React from 'react';
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
} from '@/components/ui/select';
import { FieldValues, UseFormSetValue } from 'react-hook-form';
import clsx from 'clsx';
import { CheckIcon } from 'lucide-react';
import { Typography } from '../typography';

export interface SelectOption {
	value: string;
	label: string | React.ReactElement;
}

interface Props {
	name: string;
	placeholder?: string;
	label?: string;
	options: SelectOption[];
	setValueInput?: UseFormSetValue<FieldValues>;
	defaultValue?: string;
	className?: string;
	classNameContainer?: string;
}

const findOptionByValue = (
	options: SelectOption[],
	value?: string
): SelectOption => {
	return options.find((option) => option.value === value) ?? options[0];
};

export const SelectCustom: React.FC<Props> = ({
	name,
	placeholder,
	label,
	options,
	defaultValue,
	setValueInput,
	className,
	classNameContainer,
}) => {
	const [open, setOpen] = React.useState(false);
	const [selectedValue, setSelectedValue] = React.useState<SelectOption>(
		findOptionByValue(options, defaultValue) || options[0]
	);

	// Referencias para detectar clics fuera
	const selectRef = React.useRef<HTMLDivElement>(null);
	const triggerRef = React.useRef<HTMLButtonElement>(null);
	const contentRef = React.useRef<HTMLDivElement>(null);

	// Custom hook to detect outside clicks
	React.useEffect(() => {
		const handleClickOutside = (event: MouseEvent | TouchEvent) => {
			if (!open) return;

			const target = event.target as Node;

			// Check if the click was outside of ALL the elements of the select
			const isOutsideSelect =
				selectRef.current &&
				!selectRef.current.contains(target) &&
				triggerRef.current &&
				!triggerRef.current.contains(target) &&
				contentRef.current &&
				!contentRef.current.contains(target);

			if (isOutsideSelect) {
				setOpen(false);
			}
		};

		// Use setTimeout to prevent the event from being captured immediately
		const timer = setTimeout(() => {
			document.addEventListener('mousedown', handleClickOutside);
			document.addEventListener('touchstart', handleClickOutside);
		}, 0);

		return () => {
			clearTimeout(timer);
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('touchstart', handleClickOutside);
		};
	}, [open]);

	const handleSelect = (value: string) => {
		setSelectedValue(findOptionByValue(options, value));
		setValueInput && setValueInput(name, value);
		setOpen(false);
	};

	return (
		<div ref={selectRef} className={clsx('', classNameContainer)}>
			<Select
				key={name}
				open={open}
				onOpenChange={setOpen}
				value={selectedValue.value}
				onValueChange={handleSelect}
			>
				<SelectTrigger
					ref={triggerRef}
					className={clsx(
						'justify-between w-auto h-auto gap-2 capitalize border border-gray-disabled rounded-full px-6 py-3 text-base',
						className
					)}
				>
					{selectedValue ? selectedValue.label : placeholder}
				</SelectTrigger>
				<SelectContent
					ref={contentRef}
					className="bg-white border border-gray-disabled rounded-xl shadow-shadow2 !p-0"
					// Prevent Radix from handling the automatic closing so that we control it
					onCloseAutoFocus={(e) => e.preventDefault()}
				>
					<SelectGroup className="!p-0">
						{label && <SelectLabel>{label}</SelectLabel>}
						{options.map(({ value, label }) => (
							<SelectItem
								id={value}
								key={value}
								value={value}
								className={clsx(
									'text-left justify-between w-full cursor-pointer hover:bg-grey-100 pl-6 pr-6 py-3',
									{
										'bg-grey-100': selectedValue.value === value,
									}
								)}
								// Ensure that the click on the item does not propagate
								onClick={(e) => e.stopPropagation()}
								onMouseDown={(e) => e.stopPropagation()}
								onTouchStart={(e) => e.stopPropagation()}
							>
								<div className="flex items-end justify-between w-full gap-x-4">
									<Typography type="regular-16" className="min-w-[72px]">
										{label}
									</Typography>

									{selectedValue.value === value && (
										<CheckIcon
											className="w-5 h-5 text-black"
											aria-hidden="true"
										/>
									)}
								</div>
							</SelectItem>
						))}
					</SelectGroup>
				</SelectContent>
			</Select>
		</div>
	);
};
